///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { Loader, ModelImpl, Viewer } from "@inweb/viewer-three";
import { parse, clear } from "./IFCX/render.js";

export class IFCXCloudLoader extends Loader {
  public viewer: Viewer;

  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;
  }

  override isSupport(file: any): boolean {
    return (
      typeof file === "object" &&
      typeof file.type === "string" &&
      typeof file.download === "function" &&
      /.ifcx$/i.test(file.type)
    );
  }

  override async load(file: any): Promise<this> {
    const progress = (progress: number) => {
      this.viewer.emitEvent({ type: "geometryprogress", data: progress, file });
    };

    const arrayBuffer = await file.download(progress, this.abortController.signal);
    if (!this.viewer.scene) return this;

    const textDecoder = new TextDecoder();
    const json = JSON.parse(textDecoder.decode(arrayBuffer));

    const scene = await parse(json);
    clear();

    let handle = 0;
    scene.traverse((object: any) => {
      object.userData = { handle, ...object.userData };
      handle++;
    });

    const modelImpl = new ModelImpl(scene);
    modelImpl.loader = this;
    modelImpl.viewer = this.viewer;

    this.viewer.scene.add(scene);
    this.viewer.models.push(modelImpl);

    this.viewer.syncOptions();
    this.viewer.syncOverlay();
    this.viewer.update();

    this.viewer.emitEvent({ type: "databasechunk", data: scene, file });

    return this;
  }
}
